import React, { createContext, useState, useMemo, useContext, useEffect, useCallback } from 'react';

/**
 * Theme Context - Manages theme state with localStorage persistence
 * Handles light/dark theme switching and maintains user preference
 * Similar to the original theme-context.js but adapted for React
 */

type ThemeMode = 'light' | 'dark';

// Define the context type
interface ThemeModeContextType {
    mode: ThemeMode;
    toggleColorMode: () => void;
    setTheme: (theme: ThemeMode) => void;
    isDarkTheme: () => boolean;
    isLightTheme: () => boolean;
}

// Create the context
const ThemeModeContext = createContext<ThemeModeContextType>({
    mode: 'light',
    toggleColorMode: () => {},
    setTheme: () => {},
    isDarkTheme: () => false,
    isLightTheme: () => true,
});

// Create a hook to use the context
export const useThemeMode = () => useContext(ThemeModeContext);

// Create the provider component
export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Clean up old localStorage keys on mount
    useEffect(() => {
        // Remove old duplicate keys
        if (localStorage.getItem('theme')) {
            localStorage.removeItem('theme');
        }
        if (localStorage.getItem('themeMode')) {
            localStorage.removeItem('themeMode');
        }
    }, []);

    // Get initial theme from localStorage or default to light
    const getInitialTheme = (): ThemeMode => {
        try {
            // Read from localStorage directly
            const savedTheme = localStorage.getItem('themePreference');
            if (savedTheme === 'dark' || savedTheme === 'light') {
                return savedTheme;
            }
        } catch (e) {
        }
        
        // Default to light theme on first visit
        // (ignoring system preference for consistent default experience)
            return 'light';
    };

    const [mode, setMode] = useState<ThemeMode>(() => {
        // Use lazy initialization to ensure we read from localStorage on every mount
        return getInitialTheme();
    });

    // Apply theme to document body
    const applyTheme = useCallback((theme: ThemeMode) => {
        if (!document.body) {
            // Body not ready yet, try again
            setTimeout(() => applyTheme(theme), 10);
            return;
        }
        
            const body = document.body;
        
        // Remove both classes first to avoid conflicts
        body.classList.remove('dark-theme', 'dark-mode');
        
        if (theme === 'dark') {
                body.classList.add('dark-theme');
            // Also add dark-mode for CSS compatibility
            document.documentElement.classList.add('dark-mode');
            } else {
                body.classList.remove('dark-theme');
            document.documentElement.classList.remove('dark-mode');
            }
        
        // Update theme icons
        updateThemeIcons(theme);
        
        // Dispatch themeChanged event for other components
        window.dispatchEvent(new CustomEvent('themeChanged', { 
            detail: { theme } 
        }));
    }, []);

    // Protect theme from being changed by external scripts
    useEffect(() => {
        let isApplying = false; // Flag to prevent infinite loops
        
        // Monitor for unauthorized theme changes
        const observer = new MutationObserver((mutations) => {
            // Skip if we're currently applying the theme ourselves
            if (isApplying) return;
            
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    const target = mutation.target as HTMLElement;
                    if (target === document.body || target === document.documentElement) {
                        // Check if theme classes were changed
                        const hasDarkTheme = document.body.classList.contains('dark-theme');
                        const hasDarkMode = document.documentElement.classList.contains('dark-mode');
                        const shouldBeDark = mode === 'dark';
                        
                        // If theme doesn't match our state, restore it
                        if (hasDarkTheme !== shouldBeDark || hasDarkMode !== shouldBeDark) {
                            isApplying = true;
                            // Use setTimeout to break the observer loop
                            setTimeout(() => {
                                applyTheme(mode);
                                setTimeout(() => {
                                    isApplying = false;
                                }, 50);
                            }, 0);
                        }
                    }
                }
            });
        });

        // Observe body and html for class changes
        if (document.body) {
            observer.observe(document.body, {
                attributes: true,
                attributeFilter: ['class']
            });
        }
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });

        return () => {
            observer.disconnect();
        };
    }, [mode, applyTheme]);

    // Update all theme icons
    const updateThemeIcons = (theme: ThemeMode) => {
        // Update desktop theme icon
        const allThemeIcons = document.querySelectorAll('#theme-icon');
        allThemeIcons.forEach(icon => {
            if (theme === 'dark') {
                icon.className = 'fas fa-sun';
            } else {
                icon.className = 'fas fa-moon';
            }
        });
        
        // Update mobile theme icon
        const mobileThemeIcon = document.querySelector('#theme-icon-mobile');
        if (mobileThemeIcon) {
            if (theme === 'dark') {
                mobileThemeIcon.className = 'fas fa-sun';
            } else {
                mobileThemeIcon.className = 'fas fa-moon';
            }
        }
    };

    // Set theme
    const setTheme = useCallback((theme: ThemeMode) => {
        if (theme !== 'light' && theme !== 'dark') {
            return;
        }

        setMode(theme);
        applyTheme(theme);
        
        // Save to localStorage directly
        localStorage.setItem('themePreference', theme);
    }, [applyTheme]);

    // Toggle between light and dark modes
    const toggleColorMode = useCallback(() => {
        const newTheme: ThemeMode = mode === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
    }, [mode, setTheme]);

    // Check if dark theme is active
    const isDarkTheme = useCallback(() => mode === 'dark', [mode]);

    // Check if light theme is active
    const isLightTheme = useCallback(() => mode === 'light', [mode]);

    // Apply initial theme on mount and ensure it's in sync with localStorage
    useEffect(() => {
        // Double-check localStorage to ensure consistency
        try {
            const savedTheme = localStorage.getItem('themePreference');
            if (savedTheme === 'dark' || savedTheme === 'light') {
                if (savedTheme !== mode) {
                    // localStorage has a different theme, sync it
                    setMode(savedTheme);
                    applyTheme(savedTheme);
                    return;
                }
            } else {
                // No saved preference, save the default (light) to localStorage
                localStorage.setItem('themePreference', 'light');
            }
        } catch (e) {
        }
        
        // Apply the current theme
        applyTheme(mode);
    }, []); // Only run on mount

    // Apply theme whenever mode changes
    useEffect(() => {
        applyTheme(mode);
    }, [mode, applyTheme]);

    // Listen for system theme changes - DISABLED
    // We don't auto-switch based on system preference to maintain consistent default experience
    // Users can manually toggle the theme using the theme switcher
    useEffect(() => {
        // Intentionally disabled - user must explicitly choose their theme
        // This ensures first-time visitors always see light mode
    }, []);

    // Listen for storage events to sync theme across tabs
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'themePreference' && e.newValue) {
                const newTheme = e.newValue as ThemeMode;
                if (newTheme === 'dark' || newTheme === 'light') {
                    if (newTheme !== mode) {
                        setMode(newTheme);
                        applyTheme(newTheme);
                    }
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [mode, applyTheme]);

    // Ensure theme is applied on page visibility change (when user switches tabs back)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                // Page became visible, ensure theme is correct
                try {
                    const savedTheme = localStorage.getItem('themePreference');
                    if (savedTheme === 'dark' || savedTheme === 'light') {
                        if (savedTheme !== mode) {
                            setMode(savedTheme);
                            applyTheme(savedTheme);
                        } else {
                            // Just re-apply to ensure it's still there
                            applyTheme(mode);
                        }
                    }
                } catch (e) {
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [mode, applyTheme]);

    // Provide the context value
    const contextValue = useMemo(
        () => ({
            mode,
            toggleColorMode,
            setTheme,
            isDarkTheme,
            isLightTheme,
        }),
        [mode, toggleColorMode, setTheme, isDarkTheme, isLightTheme]
    );

    return (
        <ThemeModeContext.Provider value={contextValue}>
            {children}
        </ThemeModeContext.Provider>
    );
};
